import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import AddTaskModal from "../../components/AddTaskModal";
import CoachingTabs from "./CoachingTabs";
import CoachingSkeleton from "../../components/layout/CoachingSkeleton";
import {
    Check, Clock, CheckCircle2, XCircle, X,
    ClipboardList, Filter
} from "lucide-react";

export default function CoachingSuggestions() {
    const { isDarkMode } = useTheme();
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [showAddTask, setShowAddTask] = useState(false);
    const [addTaskInitial, setAddTaskInitial] = useState(null);
    const [filter, setFilter] = useState("All");

    const fetchSuggestions = useCallback(async () => {
        try { const res = await api.get("/coach/my-suggestions"); setSuggestions(res.data.suggestions || []); } catch { setSuggestions([]); }
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await fetchSuggestions();
            setLoading(false);
        };
        load();
    }, [fetchSuggestions]);

    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const respondToSuggestion = async (suggestionId, action) => {
        setActionLoading(true);
        try {
            const res = await api.put(`/coach/my-suggestions/${suggestionId}/respond`, { action });
            showMessage(res.data.message);
            await fetchSuggestions();
            if (action === "Accepted" && res.data.suggestion) {
                const s = res.data.suggestion;
                setAddTaskInitial({
                    Title: s.SuggestionTitle || "",
                    Description: s.SuggestionDesc || "",
                    FocusAreaID: s.FocusAreaID ? String(s.FocusAreaID) : "",
                });
                setShowAddTask(true);
            }
        } catch (err) {
            showMessage(err.response?.data?.error || "Failed to respond", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const counts = {
        All: suggestions.length,
        Pending: suggestions.filter((s) => s.Status === "Pending").length,
        Accepted: suggestions.filter((s) => s.Status === "Accepted").length,
        Denied: suggestions.filter((s) => s.Status === "Denied").length,
    };

    const filtered = filter === "All" ? suggestions : suggestions.filter((s) => s.Status === filter);

    const filterTabs = [
        { key: "All", color: "emerald" },
        { key: "Pending", color: "amber", icon: Clock },
        { key: "Accepted", color: "emerald", icon: CheckCircle2 },
        { key: "Denied", color: "red", icon: XCircle },
    ];

    if (loading) {
        return <CoachingSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-6 md:py-8">
                {/* Header */}
                <header className="mb-6">
                    <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        Suggestions
                    </h1>
                    <p className={isDarkMode ? "text-slate-400" : "text-slate-600"}>
                        Review and respond to task suggestions from your coach
                    </p>
                </header>

                <CoachingTabs className="md:hidden mb-6" />

                {/* Alert */}
                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium transition-all ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>
                        {message.type === "error" ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
                        {message.text}
                    </div>
                )}

                {suggestions.length === 0 ? (
                    <div className={`rounded-2xl border p-12 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                        <div className={`w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                            <ClipboardList className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={36} />
                        </div>
                        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                            No suggestions yet
                        </h3>
                        <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            Your coach hasn't suggested any tasks yet. Check back after your next coaching session.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Summary Stats */}
                        <div className={`grid grid-cols-3 gap-3 mb-6`}>
                            <div className={`rounded-xl p-4 text-center ${isDarkMode ? "bg-amber-900/20 border border-amber-800/30" : "bg-amber-50 border border-amber-100"}`}>
                                <p className={`text-2xl font-bold ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}>{counts.Pending}</p>
                                <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? "text-amber-400/70" : "text-amber-600/70"}`}>Pending</p>
                            </div>
                            <div className={`rounded-xl p-4 text-center ${isDarkMode ? "bg-primary-900/20 border border-primary-800/30" : "bg-primary-50 border border-primary-100"}`}>
                                <p className={`text-2xl font-bold ${isDarkMode ? "text-primary-300" : "text-primary-700"}`}>{counts.Accepted}</p>
                                <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? "text-primary-400/70" : "text-primary-600/70"}`}>Accepted</p>
                            </div>
                            <div className={`rounded-xl p-4 text-center ${isDarkMode ? "bg-red-900/20 border border-red-800/30" : "bg-red-50 border border-red-100"}`}>
                                <p className={`text-2xl font-bold ${isDarkMode ? "text-red-300" : "text-red-700"}`}>{counts.Denied}</p>
                                <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? "text-red-400/70" : "text-red-600/70"}`}>Denied</p>
                            </div>
                        </div>

                        {/* Filter Tabs */}
                        <div className={`grid grid-cols-2 sm:flex gap-1 p-1 rounded-xl mb-6 ${isDarkMode ? "bg-slate-800/70" : "bg-slate-100"}`} role="tablist">
                            {filterTabs.map((t) => {
                                const active = filter === t.key;
                                return (
                                    <button
                                        key={t.key}
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setFilter(t.key)}
                                        className={`sm:flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                                            active
                                                ? isDarkMode ? "bg-slate-700 text-white shadow-sm" : "bg-white text-slate-900 shadow-sm"
                                                : isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700"
                                        }`}
                                    >
                                        {t.icon && <t.icon size={14} />}
                                        <span>{t.key}</span>
                                        <span className={`text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${
                                            active ? "bg-primary-500 text-white" : isDarkMode ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-600"
                                        }`}>
                                            {counts[t.key]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Suggestion Cards */}
                        {filtered.length === 0 ? (
                            <div className={`rounded-2xl border p-8 text-center ${isDarkMode ? "bg-slate-800/40 border-slate-700/50" : "bg-white border-slate-200"}`}>
                                <Filter className={`mx-auto mb-3 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} size={28} />
                                <p className={`text-sm font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    No {filter.toLowerCase()} suggestions
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filtered.map((s) => {
                                    const statusConfig = {
                                        Pending:  { badge: isDarkMode ? "bg-amber-900/40 text-amber-300 border-amber-800/40" : "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, accent: isDarkMode ? "border-l-amber-500" : "border-l-amber-400" },
                                        Accepted: { badge: isDarkMode ? "bg-primary-900/40 text-primary-300 border-primary-800/40" : "bg-primary-50 text-primary-700 border-primary-200", icon: CheckCircle2, accent: isDarkMode ? "border-l-primary-500" : "border-l-primary-400" },
                                        Denied:   { badge: isDarkMode ? "bg-red-900/40 text-red-300 border-red-800/40" : "bg-red-50 text-red-700 border-red-200", icon: XCircle, accent: isDarkMode ? "border-l-red-500" : "border-l-red-400" },
                                    };
                                    const cfg = statusConfig[s.Status] || statusConfig.Pending;
                                    const StatusIcon = cfg.icon;

                                    return (
                                        <div
                                            key={s.SuggestionID}
                                            className={`rounded-xl border border-l-4 ${cfg.accent} p-4 transition-all hover:shadow-sm ${
                                                isDarkMode ? "bg-slate-800/80 border-slate-700" : "bg-white border-slate-200"
                                            }`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2.5 mb-1.5">
                                                        <h4 className={`font-bold text-[15px] ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                                            {s.SuggestionTitle}
                                                        </h4>
                                                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cfg.badge}`}>
                                                            <StatusIcon size={11} /> {s.Status}
                                                        </span>
                                                    </div>
                                                    {s.SuggestionDesc && (
                                                        <p className={`text-sm leading-relaxed mb-2.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                            {s.SuggestionDesc}
                                                        </p>
                                                    )}
                                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                                        {s.FocusArea && (
                                                            <span className={`px-2.5 py-1 rounded-lg font-medium ${isDarkMode ? "bg-blue-900/30 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
                                                                {s.FocusArea}
                                                            </span>
                                                        )}
                                                        {/* A cohort's suggestions come from its program, with no coach (Oct 2: cohorts) */}
                                                        {s.CohortName && (
                                                            <span className={`${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                                                from {s.ProgramName} · {s.CohortName}
                                                            </span>
                                                        )}
                                                        {s.CoachName && (
                                                            <span className={`${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                                                by {s.CoachName}
                                                            </span>
                                                        )}
                                                        {s.SuggestedOn && (
                                                            <span className={`${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                                                &middot; {s.SuggestedOn}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {s.Status === "Pending" && (
                                                    <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                                                        <button
                                                            onClick={() => respondToSuggestion(s.SuggestionID, "Accepted")}
                                                            disabled={actionLoading}
                                                            title="Accept"
                                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 shadow-sm"
                                                        >
                                                            <Check size={13} /> Accept
                                                        </button>
                                                        <button
                                                            onClick={() => respondToSuggestion(s.SuggestionID, "Denied")}
                                                            disabled={actionLoading}
                                                            title="Deny"
                                                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                                                                isDarkMode
                                                                    ? "bg-slate-700 text-slate-300 hover:bg-red-900/40 hover:text-red-300 border border-slate-600"
                                                                    : "bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-700 border border-slate-200"
                                                            }`}
                                                        >
                                                            <X size={13} /> Deny
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            <AddTaskModal
                isOpen={showAddTask}
                onClose={() => { setShowAddTask(false); setAddTaskInitial(null); }}
                onTaskCreated={() => { setShowAddTask(false); setAddTaskInitial(null); }}
                initialTaskData={addTaskInitial}
            />
        </div>
    );
}
